import Enumerable from "linq";
import { FormatReference } from "../../format";
import { Department } from "../../department";
import { DocumentIdentifier } from "../../document";
import { ReceptionManager, ReceptionProofPeriod } from "../reception";
import { PhaseStatusKinds, FormatStatusKinds } from "../kinds";
import { ReceptionComment } from "./ReceptionComment";
import { ReceptionCommentSet } from "./ReceptionCommentSet";
import { WorkflowRouteEditorModel } from "../editor";
import { ProjectTeam } from "../../projectTeam";
import { User } from "../../user";

// 依頼詳細
export class ReceptionDetail {
  constructor(proofPeriodDefaultCount: number = 3) {
    for (let i = this.proofPeriods.length + 1; this.proofPeriods.length < proofPeriodDefaultCount; i++) {
      this.proofPeriods.push(new ReceptionProofPeriod(i));
    }   
  }
  public id: number;
  public format: FormatReference = this.createEmptyFormatReference();
  public formatStatus: FormatStatusKinds;
  public isPaper: boolean;
  public paperName: string;
  public paperCompanyName: string;
  public department: Department = this.createEmptyDepartment();
  public projectTeam: ProjectTeam = this.createEmptyProjectTeam();
  public workflowId: number;
  public document: DocumentIdentifier = this.createEmptyDocumentIdentifier();
  public sourceReceptionDetailId: number;
  public sourceDocumentId: number;
  // 閲覧権限

  // 一時退避

  public managers: ReceptionManager[] = [];
  public disease: string = null;
  public proofPeriods: ReceptionProofPeriod[] = [];

  public route: WorkflowRouteEditorModel = new WorkflowRouteEditorModel();
  public documentFixPhaseStatus: PhaseStatusKinds;
  public documentFixPahseProcessedManagers: User[] = [];

  // 入力用
  public comment: ReceptionComment = new ReceptionComment();
  public restrictComment: ReceptionComment = new ReceptionComment();
  public nextMessage: ReceptionComment = new ReceptionComment();

  // 依頼編集用
  public editComment: ReceptionComment = null;
  public editRestrictComment: ReceptionComment = null;
  public editNextMessage: ReceptionComment = null;

  // 参照用
  public commentSet: ReceptionCommentSet;

  public deleted: boolean;

  public quoted: boolean;

  get isEmpty(): boolean {
    // format
    if (this.format && !!this.format.id) { return false; }
    // projectTeam
    if (this.projectTeam && !!this.projectTeam.id) { return false; }
    // department
    if (this.department && !!this.department.id) { return false; }
    // managers
    if (this.managers && this.managers.length > 0) { return false; }
    // proofperiods
    if (Enumerable.from(this.proofPeriods).any(p => !p.isEmpty)) { return false; }
    // comment
    if (this.comment && this.comment.content) { return false; }
    // restrictComment
    if (this.restrictComment && this.restrictComment.content) { return false; }
    // nextmessage
    if (this.nextMessage && this.nextMessage.content) { return false; }

    return true;
  }

  clone(): ReceptionDetail {
    // 暫定対応
    const newObj = new ReceptionDetail();
    newObj.id = this.id;
    newObj.format = this.format;
    newObj.formatStatus = this.formatStatus;
    newObj.isPaper = this.isPaper;
    newObj.paperName = this.paperName;
    newObj.paperCompanyName = this.paperCompanyName;
    newObj.department = this.department;
    newObj.projectTeam = this.projectTeam;
    newObj.workflowId = this.workflowId;
    newObj.document = this.document;
    newObj.sourceReceptionDetailId = this.sourceReceptionDetailId;
    newObj.sourceDocumentId = this.sourceDocumentId;
    newObj.managers = this.managers;
    newObj.disease = this.disease;
    newObj.proofPeriods = this.proofPeriods;

    newObj.route = this.route;
    newObj.documentFixPhaseStatus = this.documentFixPhaseStatus;
    newObj.documentFixPahseProcessedManagers = this.documentFixPahseProcessedManagers;

    newObj.comment = this.comment;
    newObj.restrictComment = this.restrictComment;
    newObj.nextMessage = this.nextMessage;

    newObj.editComment = this.editComment;
    newObj.editRestrictComment = this.editRestrictComment;
    newObj.editNextMessage = this.editNextMessage;

    newObj.commentSet = this.commentSet;

    newObj.deleted = this.deleted;
    newObj.quoted = this.quoted;

    return newObj;
  }

  copy(): ReceptionDetail {
    // 暫定対応
    const newObj = new ReceptionDetail();
    // newObj.id = this.id; これはコピーしちゃだめ！
    newObj.format = this.cloneFormatReference();
    newObj.formatStatus = this.formatStatus;
    newObj.isPaper = this.isPaper;
    newObj.projectTeam = this.cloneProjectTeam();
    newObj.department = this.cloneDepartment();
    // newObj.workflowId = this.workflowId;
    // newObj.document = this.document;
    // newObj.sourceReceptionDetailId = this.sourceReceptionDetailId;
    newObj.managers = this.cloneManagers();
    newObj.proofPeriods = this.cloneProofPeriods();
    // newObj.comments = this.comments; // TODO
    // newObj.restrictComments = this.restrictComments; // TODO
    newObj.route = this.route.clone();
    // newObj.documentFixPhaseStatus = this.documentFixPhaseStatus;
    // newObj.nextMessage = this.nextMessage; // TODO
    // newObj.deleted = this.deleted;
    // newObj.quoted = this.quoted;

    newObj.comment = this.comment.clone();
    newObj.restrictComment = this.restrictComment.clone();
    newObj.nextMessage = this.nextMessage.clone();

    return newObj;
  }

  private createEmptyFormatReference(): FormatReference {
    return new FormatReference(null, null, null, null);
  }

  private createEmptyDocumentIdentifier(): DocumentIdentifier {
    return new DocumentIdentifier(null, null);
  }

  private createEmptyProjectTeam(): ProjectTeam {
    return new ProjectTeam(null, null, null, null, [], false);
  }

  private createEmptyDepartment(): Department {
    return new Department(null, null, null);
  }

  private cloneFormatReference(): FormatReference {
    return this.format
      ? new FormatReference(
          this.format.id,
          this.format.name,
          this.format.previewSource,
          this.format.thumbnailSource,
          this.format.reportTypeId,
          this.format.masterCode,
          this.format.tag,
          this.format.company,
          this.format.categoryType,
          this.format.writeMethodType,
          this.format.startDateTime,
          this.format.endDateTime,
          this.format.expired,
          this.format.deleted,
          this.format.editedName,
          this.format.originalName,
          this.format.note,
          this.format.quoteBehaviorKind)
      : this.createEmptyFormatReference();
  }

  private cloneProjectTeam(): ProjectTeam {
    return this.projectTeam ? new ProjectTeam(this.projectTeam.id, this.projectTeam.name, this.projectTeam.kanaName, this.projectTeam.department, this.projectTeam.members, this.projectTeam.isDeleted) : this.createEmptyProjectTeam();
  }

  private cloneDepartment(): Department {
    return this.department ? new Department(this.department.id, this.department.name, this.department.shortName) : this.createEmptyDepartment();
  }

  private cloneManagers(): ReceptionManager[] {
    return this.managers ? Enumerable.from(this.managers)
      .select(m => m.clone())
      .toArray() : [];
  }

  private cloneProofPeriods(): ReceptionProofPeriod[] {
    return this.proofPeriods ? Enumerable.from(this.proofPeriods)
      .select(p => p.clone(p.displayOrder))
      .toArray() : [new ReceptionProofPeriod(1), new ReceptionProofPeriod(2), new ReceptionProofPeriod(3)];
  }
}
